import { useState } from "react";
import { albertaToday } from "../../../utils/albertaTime";
import { YES, NO, YES_OR_NO, isYes } from "./pageFields";
import {
    PERMISSION_LABEL, ORDERED_LABEL, UPLOADED_LABEL, PERMISSION_PLACEHOLDER, ORDERED_PLACEHOLDER, UPLOADED_PLACEHOLDER,
    ORDER_DATE_LABEL, UPLOAD_DATE_LABEL, DISCLOSURE_STATUS_LABEL, PACKAGE_REQUEST_LABEL,
    OTHER_EXPLANATION_REQUIRED_MESSAGE, requiredMessage, isOther, isBlank, choiceNamed, disclosureDateProblem, enteredDate, disclosureDateToSave,
} from "./disclosureInformation";

// One of the three Yes/No questions. Empty until the interaction has loaded; the error says the answer is missing.
const useYesOrNoQuestion = (label, placeholder) => {
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState(false);
    return {
        label, placeholder, answer, error,
        load: (loaded) => setAnswer(loaded),
        /** Takes the answer the select names and returns it, so a caller that needs it does not look it up again. */
        choose: (event) => {
            const chosen = YES_OR_NO.find((item) => item?.label === event.target.value);
            setAnswer(chosen);
            if (chosen) setError(false);
            return chosen;
        },
        // the answer is missing: the field says so and the save is blocked
        isMissing: () => {
            if (answer) return false;
            setError(true);
            return true;
        },
        // the page's automatic save checks the answer without telling the user
        clearError: () => setError(false),
        toSave: () => (answer ? answer.value : null),
    };
};

// One of the two disclosure dates (stories 11030/11031): the day, why it blocks a save (or null), and whether the page
// filled it in itself. The acceptance criteria want the day the question was saved as Yes, so a date the page filled
// in is brought up to the day of the save; a date the user typed or picked is theirs.
const useDisclosureDate = (label) => {
    const [date, setDate] = useState(null);
    const [error, setError] = useState(null);
    const [isAutomatic, setIsAutomatic] = useState(false);
    return {
        label, date, error,
        load: (stored) => {
            setDate(stored ?? null);
            setIsAutomatic(false);
        },
        // A Yes shows the date filled with today unless one is already held; anything else clears it (the API records
        // the cleared value in the audit log)
        followAnswer: (answer) => {
            if (isYes(answer)) {
                if (!date) {
                    setDate(albertaToday());
                    setIsAutomatic(true);
                }
            } else {
                setDate(null);
                setIsAutomatic(false);
            }
            setError(null);
        },
        // A typed or picked date is kept as entered so the user can see and fix it; the message says why it blocks saving
        onChange: (value) => {
            setIsAutomatic(false);
            if (value === null || value === undefined || value === '') {
                setDate(null);
                setError(null);
                return;
            }
            setDate(enteredDate(value));
            setError(disclosureDateProblem(label, value));
        },
        // While its question is Yes the date must be present and pass the rules; the message is put on the field so
        // the user sees what blocks the save
        isInvalid: (question, { silent = false } = {}) => {
            if (!isYes(question)) return false;
            const problem = date ? disclosureDateProblem(label, date) : requiredMessage(label);
            if (!silent) setError(problem);
            return problem !== null;
        },
        toSave: (question, showDisclosureInformation) => disclosureDateToSave(isAutomatic ? albertaToday() : date, question, showDisclosureInformation),
        // A date the page filled in itself becomes the day of the save
        refreshIfAutomatic: () => {
            if (isAutomatic) setDate(albertaToday());
        },
        // The save stored it: from now on it is a saved date, which a later save on another day must not move
        settle: () => setIsAutomatic(false),
    };
};

/**
 * The Disclosure Information section's state and rules: the three disclosure questions (shown in Lawyer Information
 * on the legacy layout), the two dates, Disclosure Status with the explanation an "Other" status needs, and
 * Disclosure Package Request with the attempt the server holds. The page loads it, asks it whether a save may go ahead
 * and what a save sends, and tells it what the server stored.
 */
export function useDisclosureInformation() {
    // The API decides from the created date which layout an interaction uses. Null until the interaction has loaded,
    // so neither layout is drawn and then redrawn when the answer arrives.
    const [showDisclosureInformation, setShowDisclosureInformation] = useState(null);
    const permission = useYesOrNoQuestion(PERMISSION_LABEL, PERMISSION_PLACEHOLDER);
    const ordered = useYesOrNoQuestion(ORDERED_LABEL, ORDERED_PLACEHOLDER);
    const uploaded = useYesOrNoQuestion(UPLOADED_LABEL, UPLOADED_PLACEHOLDER);
    const orderDate = useDisclosureDate(ORDER_DATE_LABEL);
    const uploadDate = useDisclosureDate(UPLOAD_DATE_LABEL);
    // Stories 11032/11033: the choices come from the API's metadata; a message in an error state blocks the save
    const [statusChoices, setStatusChoices] = useState([]);
    const [packageRequestChoices, setPackageRequestChoices] = useState([]);
    const [disclosureStatus, setDisclosureStatus] = useState(null);
    const [disclosureStatusError, setDisclosureStatusError] = useState(null);
    const [otherExplanation, setOtherExplanation] = useState("");
    const [otherExplanationError, setOtherExplanationError] = useState(null);
    const [packageRequest, setPackageRequest] = useState(null);
    const [packageRequestError, setPackageRequestError] = useState(null);
    // Whether the user has changed anything in the section since the interaction was loaded. The page's automatic
    // save leaves a touched section alone: only the user's Save stores it, as story 11032 says. A save does not
    // clear it (the automatic save would run straight after the user's); only load does.
    // Never set on the legacy layout, whose questions the automatic save may still store.
    const [changedSinceLoad, setChangedSinceLoad] = useState(false);
    const usesSection = showDisclosureInformation === true;
    const touch = () => { if (usesSection) setChangedSinceLoad(true); };

    const takeChoices = (metadata) => {
        setStatusChoices(metadata.disclosureStatuses ?? []);
        setPackageRequestChoices(metadata.disclosurePackageRequests ?? []);
    };

    const load = (interaction, metadata) => {
        uploaded.load(interaction.isDisclosureUploaded === true ? YES : NO);
        ordered.load(interaction.isDisclosureOrdered === true ? YES : NO);
        permission.load(interaction.hasPermissionToOrderDisclosure === true ? YES : (interaction.hasPermissionToOrderDisclosure === false ? NO : null));
        orderDate.load(interaction.disclosureOrderDate);
        uploadDate.load(interaction.disclosureUploadDate);
        // A status the API no longer offers (retired) is still shown, with the label of the status record the API sends
        // alongside the id (the one place it sends the label, like statusDetails for the Interaction Status)
        const offeredStatus = (metadata?.disclosureStatuses ?? []).find((choice) => choice.id === interaction.disclosureStatus);
        const heldStatus = interaction.disclosureStatus ? { id: interaction.disclosureStatus, label: interaction.disclosureStatusDetails?.label ?? "" } : null;
        setDisclosureStatus(offeredStatus ?? heldStatus);
        setDisclosureStatusError(null);
        setOtherExplanation(interaction.disclosureStatusOtherExplanation ?? "");
        setOtherExplanationError(null);
        setPackageRequest((metadata?.disclosurePackageRequests ?? []).find((choice) => choice.value === interaction.disclosurePackageRequest) ?? null);
        setPackageRequestError(null);
        setShowDisclosureInformation(interaction.showDisclosureInformation === true);
        setChangedSinceLoad(false);
    };

    // Stories 11032/11033: while "Disclosure has been ordered?" is No the status and the package request are not
    // mandatory and default to blank, so a No clears both, whether a choice was made a moment ago or stored by an
    // earlier save (the stories, as revised on 21 October 2026: a saved value is cleared too, and the user's Save
    // then stores blank). Their messages go with them.
    const clearChoices = () => {
        setDisclosureStatus(null);
        setDisclosureStatusError(null);
        setOtherExplanation("");
        setOtherExplanationError(null);
        setPackageRequest(null);
        setPackageRequestError(null);
    };

    // Legacy-layout interactions keep their stored dates untouched when a question changes
    const onOrderedChanged = (event) => {
        touch();
        const answer = ordered.choose(event);
        if (usesSection) {
            orderDate.followAnswer(answer);
            if (!isYes(answer)) clearChoices();
        }
    };
    const onUploadedChanged = (event) => {
        touch();
        const answer = uploaded.choose(event);
        if (usesSection) uploadDate.followAnswer(answer);
    };
    const onPermissionChanged = (event) => {
        touch();
        permission.choose(event);
    };

    // Story 11033: a status other than Other has no explanation, so the text goes the moment the status changes
    const onDisclosureStatusChanged = (event) => {
        touch();
        const chosen = choiceNamed(statusChoices, event.target.value);
        setDisclosureStatus(chosen);
        setDisclosureStatusError(null);
        if (!isOther(chosen)) {
            setOtherExplanation("");
            setOtherExplanationError(null);
        }
    };
    const onOtherExplanationChanged = (event) => {
        touch();
        setOtherExplanation(event.target.value);
        setOtherExplanationError(null);
    };
    const onPackageRequestChanged = (event) => {
        touch();
        setPackageRequest(choiceNamed(packageRequestChoices, event.target.value));
        setPackageRequestError(null);
    };

    // Says whether the save may go ahead, and puts each message on its field. The page's automatic save checks
    // silently: the section's messages then wait for the user's Save, and one a Save put there stays.
    const validate = ({ silent = false } = {}) => {
        let valid = true;
        if (uploaded.isMissing()) valid = false;
        if (ordered.isMissing()) valid = false;
        if (usesSection) {
            if (orderDate.isInvalid(ordered.answer, { silent })) valid = false;
            if (uploadDate.isInvalid(uploaded.answer, { silent })) valid = false;
            // Stories 11032/11033: both choices are mandatory once disclosure has been ordered; Other needs its explanation
            if (isYes(ordered.answer) && !disclosureStatus) { if (!silent) setDisclosureStatusError(requiredMessage(DISCLOSURE_STATUS_LABEL)); valid = false; }
            if (isYes(ordered.answer) && !packageRequest) { if (!silent) setPackageRequestError(requiredMessage(PACKAGE_REQUEST_LABEL)); valid = false; }
            if (isOther(disclosureStatus) && isBlank(otherExplanation)) { if (!silent) setOtherExplanationError(OTHER_EXPLANATION_REQUIRED_MESSAGE); valid = false; }
        }
        if (permission.isMissing()) valid = false;
        return valid;
    };

    // The page's automatic save checks the interaction silently: it takes back the messages its check put on the three
    // questions, as it does for the page's other required fields
    const clearQuestionMessages = () => {
        uploaded.clearError();
        ordered.clearError();
        permission.clearError();
    };

    // What a save sends of the section. The status travels as its id and the package request as its attempt number;
    // the explanation only travels with an Other status. The layout flag is the API's, never sent as data to store.
    const toSave = () => ({
        isDisclosureUploaded: uploaded.toSave(),
        isDisclosureOrdered: ordered.toSave(),
        hasPermissionToOrderDisclosure: permission.toSave(),
        disclosureOrderDate: orderDate.toSave(ordered.answer, showDisclosureInformation),
        disclosureUploadDate: uploadDate.toSave(uploaded.answer, showDisclosureInformation),
        disclosureStatus: disclosureStatus?.id ?? null,
        disclosureStatusOtherExplanation: isOther(disclosureStatus) && !isBlank(otherExplanation) ? otherExplanation.trim() : null,
        disclosurePackageRequest: packageRequest?.value ?? null,
    });

    const refreshAutomaticDates = () => {
        orderDate.refreshIfAutomatic();
        uploadDate.refreshIfAutomatic();
    };
    const settleAutomaticDates = () => {
        orderDate.settle();
        uploadDate.settle();
    };

    return {
        showDisclosureInformation,
        permission: { ...permission, onChange: onPermissionChanged },
        ordered: { ...ordered, onChange: onOrderedChanged },
        uploaded: { ...uploaded, onChange: onUploadedChanged },
        orderDate: { ...orderDate, onChange: (value) => { touch(); orderDate.onChange(value); } },
        uploadDate: { ...uploadDate, onChange: (value) => { touch(); uploadDate.onChange(value); } },
        changedSinceLoad,
        statusChoices, disclosureStatus, disclosureStatusError, onDisclosureStatusChanged,
        otherExplanation, otherExplanationError, onOtherExplanationChanged,
        packageRequestChoices, packageRequest, packageRequestError, onPackageRequestChanged,
        takeChoices, load, validate, clearQuestionMessages, toSave, refreshAutomaticDates, settleAutomaticDates,
    };
}
